'use client';

import React, { useState, useRef } from 'react';
import NextImage from 'next/image';
import { MaleAttractivenessData, AttractivenessReport, MaleAttractivenessReportGenerator } from '@/lib/maleAttractivenessReportGenerator';

interface MaleAttractivenessResultProps {
  data: MaleAttractivenessData;
  imageUrl: string;
  onRetry: () => void;
}

export default function MaleAttractivenessResult({ data, imageUrl, onRetry }: MaleAttractivenessResultProps) {
  const report = MaleAttractivenessReportGenerator.generateReport(data);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);
  const detailedAnalysisRef = useRef<HTMLDivElement>(null);

  const getScoreColor = (score: number) => {
    if (score >= 8.5) return 'text-green-600';
    if (score >= 7.0) return 'text-blue-600';
    if (score >= 5.5) return 'text-yellow-600';
    if (score >= 4.0) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBarColor = (score: number) => {
    if (score >= 8.5) return 'bg-green-500';
    if (score >= 7.0) return 'bg-blue-500';
    if (score >= 5.5) return 'bg-yellow-500';
    if (score >= 4.0) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High': return 'bg-red-100 text-red-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to draw rounded rectangle
  const drawRoundedRect = (ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) => {
    const path = new Path2D();
    path.roundRect(x, y, width, height, radius);
    return path;
  };

  // Generate and download report image
  const generateReportImage = async () => {
    setIsGeneratingReport(true);
    try {
      const canvas = document.createElement('canvas');
      const width = 900;
      const height = 500;
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Failed to get canvas context');
        return;
      }

      // Background gradient (masculine dark theme)
      const bgGradient = ctx.createLinearGradient(0, 0, width, height);
      bgGradient.addColorStop(0, '#1a1a1a');
      bgGradient.addColorStop(0.5, '#2d3748');
      bgGradient.addColorStop(1, '#1a202c');
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, width, height);

      // Main content panel (dark semi-transparent)
      const panelX = 30;
      const panelY = 30;
      const panelWidth = width - 60;
      const panelHeight = height - 60;

      ctx.save();
      ctx.fillStyle = 'rgba(55, 65, 81, 0.95)';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
      ctx.shadowBlur = 25;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 15;
      const panelPath = drawRoundedRect(ctx, panelX, panelY, panelWidth, panelHeight, 20);
      ctx.fill(panelPath);
      ctx.restore();

      // Title
      ctx.font = 'bold 32px Arial';
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText('MALE ATTRACTIVENESS ANALYSIS', width / 2, panelY + 20);

      // Layout sections
      const leftSectionX = panelX + 40;
      const leftSectionWidth = 200;
      const centerSectionX = leftSectionX + leftSectionWidth + 40;
      const centerSectionWidth = 160;
      const rightSectionX = centerSectionX + centerSectionWidth + 40;
      const rightSectionWidth = panelWidth - (rightSectionX - panelX) - 40;

      // User photo section (left)
      const photoSize = 130;
      const photoX = leftSectionX + (leftSectionWidth - photoSize) / 2;
      const photoY = panelY + 70;

      if (imageUrl) {
        try {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.src = imageUrl;
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
          });

          // Draw circular photo background
          ctx.save();
          ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
          ctx.shadowBlur = 15;
          ctx.shadowOffsetX = 0;
          ctx.shadowOffsetY = 8;
          ctx.beginPath();
          ctx.arc(photoX + photoSize/2, photoY + photoSize/2, photoSize/2, 0, Math.PI * 2);
          ctx.fillStyle = '#4a5568';
          ctx.fill();
          ctx.restore();

          // Clip and draw user photo
          ctx.save();
          ctx.beginPath();
          ctx.arc(photoX + photoSize/2, photoY + photoSize/2, photoSize/2 - 3, 0, Math.PI * 2);
          ctx.clip();

          // Calculate image scaling to fill circle
          const imgAspect = img.width / img.height;
          let drawWidth, drawHeight;
          if (imgAspect > 1) {
            drawHeight = photoSize - 6;
            drawWidth = drawHeight * imgAspect;
          } else {
            drawWidth = photoSize - 6;
            drawHeight = drawWidth / imgAspect;
          }

          const drawX = photoX + 3 + (photoSize - 6 - drawWidth) / 2;
          const drawY = photoY + 3 + (photoSize - 6 - drawHeight) / 2;

          ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
          ctx.restore();

          // Photo border
          ctx.beginPath();
          ctx.arc(photoX + photoSize/2, photoY + photoSize/2, photoSize/2, 0, Math.PI * 2);
          ctx.strokeStyle = '#f59e0b';
          ctx.lineWidth = 3;
          ctx.stroke();
        } catch (error) {
          console.log('Could not load user image, skipping...');
        }
      }

      // Overall score circle (center)
      const scoreX = centerSectionX + centerSectionWidth / 2;
      const scoreY = panelY + 135;
      const circleRadius = 50;

      // Background circle
      ctx.beginPath();
      ctx.arc(scoreX, scoreY, circleRadius, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(37, 99, 235, 0.3)';
      ctx.lineWidth = 8;
      ctx.stroke();

      // Progress circle
      const overallScore = Number(data.overallAttractiveness?.score) || 0;
      const progress = (overallScore / 10) * Math.PI * 2;
      const gradientCircle = ctx.createLinearGradient(scoreX - circleRadius, scoreY, scoreX + circleRadius, scoreY);
      gradientCircle.addColorStop(0, '#2563eb');
      gradientCircle.addColorStop(1, '#f59e0b');

      ctx.beginPath();
      ctx.arc(scoreX, scoreY, circleRadius, -Math.PI / 2, progress - Math.PI / 2);
      ctx.strokeStyle = gradientCircle;
      ctx.lineWidth = 8;
      ctx.lineCap = 'round';
      ctx.stroke();

      // Score text
      ctx.font = 'bold 32px Arial';
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(overallScore.toFixed(1), scoreX, scoreY);

      // Add confidence indicator below the photo (now that overallScore is defined)
      const confidenceY = photoY + photoSize + 20;
      const confidence = overallScore >= 8 ? 'High' : overallScore >= 6 ? 'Medium' : 'Developing';

      ctx.font = 'bold 12px Arial';
      ctx.fillStyle = '#f59e0b';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Analysis Confidence', photoX + photoSize/2, confidenceY);

      ctx.font = '14px Arial';
      ctx.fillStyle = '#d1d5db';
      ctx.fillText(confidence, photoX + photoSize/2, confidenceY + 18);

      // Add percentile comparison below confidence
      const percentile = Math.min(95, Math.max(5, Math.round((overallScore / 10) * 85 + 10)));
      ctx.font = 'bold 12px Arial';
      ctx.fillStyle = '#f59e0b';
      ctx.fillText('Performance', photoX + photoSize/2, confidenceY + 45);

      ctx.font = '14px Arial';
      ctx.fillStyle = '#d1d5db';
      ctx.fillText(`Top ${100-percentile}%`, photoX + photoSize/2, confidenceY + 63);

      // Metrics (right section)
      const metrics = [
        { label: 'Masculinity', value: Number(data.masculinityIndex?.score) || 0 },
        { label: 'Symmetry', value: Number(data.facialSymmetry?.score) || 0 },
        { label: 'Jawline', value: Number(data.jawlineStrength?.score) || 0 },
        { label: 'Skin Quality', value: Number(data.skinQuality?.score) || 0 }
      ];

      const metricsStartY = panelY + 85;
      const metricsSpacing = 38;
      const barWidth = rightSectionWidth - 90;
      const barHeight = 14;

      metrics.forEach((metric, index) => {
        const y = metricsStartY + index * metricsSpacing;

        // Label
        ctx.font = 'bold 14px Arial';
        ctx.fillStyle = '#e5e7eb';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(metric.label, rightSectionX, y);

        // Progress bar background
        ctx.fillStyle = 'rgba(75, 85, 99, 0.5)';
        const bgBarPath = drawRoundedRect(ctx, rightSectionX, y + 15, barWidth, barHeight, 6);
        ctx.fill(bgBarPath);

        // Progress bar
        const barGradient = ctx.createLinearGradient(rightSectionX, y + 15, rightSectionX + barWidth, y + 27);
        barGradient.addColorStop(0, '#2563eb');
        barGradient.addColorStop(1, '#f59e0b');
        ctx.fillStyle = barGradient;

        const progress = (metric.value / 10) * barWidth;
        const progressPath = drawRoundedRect(ctx, rightSectionX, y + 15, progress, barHeight, 6);
        ctx.fill(progressPath);

        // Score value
        ctx.font = 'bold 16px Arial';
        ctx.fillStyle = '#f59e0b';
        ctx.textAlign = 'right';
        ctx.fillText((Number(metric.value) || 0).toFixed(1), rightSectionX + barWidth + 50, y + 21);
      });

      // Overall score label (under circle)
      ctx.font = 'bold 16px Arial';
      ctx.fillStyle = '#e5e7eb';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText('OVERALL SCORE', scoreX, scoreY + circleRadius + 15);

      // User photo label (under photo)
      ctx.font = 'bold 14px Arial';
      ctx.fillStyle = '#e5e7eb';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText('YOUR PHOTO', photoX + photoSize/2, photoY + photoSize + 15);

      // Add confidence and performance info below the photo label
      const infoStartY = photoY + photoSize + 40; // Start below "YOUR PHOTO" label

      ctx.font = 'bold 11px Arial';
      ctx.fillStyle = '#f59e0b';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Analysis Confidence', photoX + photoSize/2, infoStartY);

      ctx.font = '12px Arial';
      ctx.fillStyle = '#d1d5db';
      ctx.fillText(confidence, photoX + photoSize/2, infoStartY + 15);

      // Add percentile comparison below confidence
      const percentileInfo = Math.min(95, Math.max(5, Math.round((overallScore / 10) * 85 + 10)));
      ctx.font = 'bold 11px Arial';
      ctx.fillStyle = '#f59e0b';
      ctx.fillText('Performance', photoX + photoSize/2, infoStartY + 35);

      ctx.font = '12px Arial';
      ctx.fillStyle = '#d1d5db';
      ctx.fillText(`Top ${100-percentileInfo}%`, photoX + photoSize/2, infoStartY + 50);

      // Improvement suggestions section
      const suggestionsY = panelY + 320; // Moved down to accommodate the new info

      // Section title
      ctx.font = 'bold 18px Arial';
      ctx.fillStyle = '#f59e0b';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('ENHANCEMENT RECOMMENDATIONS', width / 2, suggestionsY);

      // Generate suggestions based on lowest scoring metrics
      const sortedMetrics = metrics.sort((a, b) => (Number(a.value) || 0) - (Number(b.value) || 0));
      const suggestions = [];

      if ((Number(sortedMetrics[0]?.value) || 0) < 7) {
        const lowestMetric = sortedMetrics[0];
        if (lowestMetric.label === 'Masculinity') {
          suggestions.push('• Consider strength training to enhance facial structure');
          suggestions.push('• Maintain good posture and confident body language');
        } else if (lowestMetric.label === 'Symmetry') {
          suggestions.push('• Professional grooming can enhance facial symmetry');
          suggestions.push('• Consider consulting a dermatologist for skin care');
        } else if (lowestMetric.label === 'Jawline') {
          suggestions.push('• Facial exercises may help define jawline');
          suggestions.push('• Maintain a healthy weight for better facial definition');
        } else if (lowestMetric.label === 'Skin Quality') {
          suggestions.push('• Establish a consistent skincare routine');
          suggestions.push('• Stay hydrated and maintain a healthy diet');
        }
      } else {
        suggestions.push('• Maintain your current grooming routine');
        suggestions.push('• Continue healthy lifestyle habits');
        suggestions.push('• Confidence is your greatest asset');
      }

      // Draw suggestions
      ctx.font = '14px Arial';
      ctx.fillStyle = '#d1d5db';
      ctx.textAlign = 'left';
      let suggestionY = suggestionsY + 35;

      suggestions.forEach((suggestion, index) => {
        if (index < 3) { // Limit to 3 suggestions
          ctx.fillText(suggestion, panelX + 50, suggestionY);
          suggestionY += 22;
        }
      });

      // Attractiveness type
      ctx.font = 'bold 26px Arial';
      ctx.fillStyle = '#f59e0b';
      ctx.textAlign = 'center';
      const attractivenessType = report.attractivenessType?.type || 'Alpha Male';
      ctx.fillText(`TYPE: ${attractivenessType.toUpperCase()}`, width / 2, height - 90);

      // Watermark
      ctx.font = 'bold 14px Arial';
      ctx.fillStyle = 'rgba(229, 231, 235, 0.8)';
      ctx.textAlign = 'center';
      ctx.fillText('Male Attractiveness Test - aiattractiveness.com', width / 2, height - 45);

      // Convert to blob and download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `male-attractiveness-report-${Date.now()}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }, 'image/png', 0.95);

    } catch (error) {
      console.error('Error generating report image:', error);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-2xl shadow-xl">
      {/* Header Section */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Male Attractiveness Analysis Results
        </h1>
        <p className="text-gray-600">
          Comprehensive AI-powered masculine appeal assessment
        </p>
      </div>

      {/* 使用2×2网格布局，确保四个模块完美对齐 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* 第一行第一列：用户图片 */}
        <div className="bg-white rounded-xl overflow-hidden shadow-md">
          <div className="relative w-full aspect-square">
            <NextImage
              src={imageUrl}
              alt="Analyzed photo"
              fill
              className="object-cover"
            />
          </div>
        </div>
        
        {/* First row, second column: Score information */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl shadow-md h-full flex flex-col justify-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {report.summary.title}
          </h2>
          <div className="flex items-center gap-4 mb-4">
            <div className="text-4xl font-bold text-blue-600">
              {data.overallAttractiveness.score}/10
            </div>
            <div className="text-lg font-semibold text-gray-700">
              {data.overallAttractiveness.rating}
            </div>
          </div>
          <p className="text-gray-700 mb-4">
            {report.summary.description}
          </p>
          
          {/* Highlights */}
          <div className="space-y-2">
            <h3 className="font-semibold text-gray-900">Key Highlights:</h3>
            <ul className="space-y-1">
              {report.summary.highlights.map((highlight, index) => (
                <li key={index} className="flex items-center text-sm text-gray-700">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  {highlight}
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* Second row, first column: Analysis Details */}
        <div className="bg-blue-50 p-6 rounded-xl shadow-md border border-blue-100">
          <h3 className="text-xl font-bold text-blue-600 mb-4">Analysis Details</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="border-l-4 border-blue-500 pl-3 py-1">
              <span className="text-blue-700 font-medium block">Age:</span>
              <div className="font-bold text-gray-900 text-lg">{data.age} years</div>
            </div>
            <div className="border-l-4 border-purple-500 pl-3 py-1">
              <span className="text-purple-700 font-medium block">Gender:</span>
              <div className="font-bold text-gray-900 text-lg">{data.gender}</div>
            </div>
            <div className="border-l-4 border-indigo-500 pl-3 py-1">
              <span className="text-indigo-700 font-medium block">Age Category:</span>
              <div className="font-bold text-gray-900 text-lg">{data.ageAppropriateness.category}</div>
            </div>
            <div className="border-l-4 border-cyan-500 pl-3 py-1">
              <span className="text-cyan-700 font-medium block">Masculinity Level:</span>
              <div className="font-bold text-gray-900 text-lg">{data.masculinityIndex.level}</div>
            </div>
          </div>
        </div>
        
        {/* Second row, second column: Attractiveness Type */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl shadow-md">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Your Attractiveness Type: {report.attractivenessType.type}
          </h3>
          <p className="text-gray-700 mb-4">
            {report.attractivenessType.description}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Strengths:</h4>
              <ul className="space-y-1">
                {report.attractivenessType.strengths.map((strength, index) => (
                  <li key={index} className="text-sm text-gray-700">
                    • {strength}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Characteristics:</h4>
              <ul className="space-y-1">
                {report.attractivenessType.characteristics.map((char, index) => (
                  <li key={index} className="text-sm text-gray-700">
                    • {char}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>



      {/* Detailed Analysis Expandable Card - Left Aligned */}
      <div className="mb-8">
        <div 
          onClick={() => {
            setShowDetailedAnalysis(!showDetailedAnalysis);
            if (!showDetailedAnalysis && detailedAnalysisRef.current) {
              setTimeout(() => {
                detailedAnalysisRef.current?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }
          }}
          className="bg-gradient-to-r from-slate-50 to-gray-50 border border-slate-200 rounded-xl p-4 sm:p-6 cursor-pointer hover:shadow-lg transition-all duration-300 hover:from-slate-100 hover:to-gray-100 active:scale-[0.98] w-full"
        >
          {/* Mobile Layout - Simplified */}
          <div className="block sm:hidden">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-slate-600 to-slate-700 rounded-full flex items-center justify-center shadow-md">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900">View Detailed Analysis Report</h3>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-600 font-medium text-sm">
                  {showDetailedAnalysis ? 'Collapse' : 'Expand'}
                </span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className={`h-5 w-5 text-slate-600 transition-transform duration-300 ${showDetailedAnalysis ? 'rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden sm:flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-slate-600 to-slate-700 rounded-full flex items-center justify-center shadow-md">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">View Detailed Analysis Report</h3>
                <p className="text-gray-600 text-sm">In-depth insights into scores and personalized improvement recommendations</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-600 font-medium">
                {showDetailedAnalysis ? 'Collapse' : 'Expand'}
              </span>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className={`h-6 w-6 text-slate-600 transition-transform duration-300 ${showDetailedAnalysis ? 'rotate-180' : ''}`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
        
        {/* Collapsible content - 优化过渡效果 */}
        <div 
          ref={detailedAnalysisRef}
          className={`mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 transition-all duration-500 ease-in-out overflow-hidden ${
            showDetailedAnalysis 
              ? 'max-h-[5000px] opacity-100 border-t border-gray-200 pt-6' 
              : 'max-h-0 opacity-0 pointer-events-none'
          }`}
        >
          {report.detailedAnalysis.map((analysis, index) => (
            <div key={index} className="bg-gray-50 p-6 rounded-xl">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  {analysis.category}
                </h3>
                <span className={`text-xl font-bold ${getScoreColor(analysis.score)}`}>
                  {analysis.score}/10
                </span>
              </div>
              
              {/* Score Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                <div 
                  className={`h-2 rounded-full ${getScoreBarColor(analysis.score)}`}
                  style={{ width: `${(analysis.score / 10) * 100}%` }}
                ></div>
              </div>
              
              <p className="text-gray-700 text-sm mb-3">
                {analysis.feedback}
              </p>
              
              <div className="space-y-2">
                <div className="text-xs text-gray-600">
                  <strong>Impact:</strong> {analysis.impact}
                </div>
                <div>
                  <strong className="text-xs text-gray-900">Tips:</strong>
                  <ul className="mt-1 space-y-1">
                    {analysis.tips.map((tip, tipIndex) => (
                      <li key={tipIndex} className="text-xs text-gray-600">
                        • {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Personalized Advice - also part of detailed analysis */}
      <div className={`mb-8 transition-all duration-500 ease-in-out overflow-hidden ${
        showDetailedAnalysis ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Personalized Improvement Plan</h2>
        <div className="space-y-4">
          {report.personalizedAdvice.map((advice, index) => (
            <div key={index} className="bg-white border border-gray-200 p-6 rounded-xl">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  {advice.category}
                </h3>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(advice.priority)}`}>
                  {advice.priority} Priority
                </span>
              </div>
              
              <p className="text-gray-700 mb-4">
                {advice.suggestion}
              </p>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Action Items:</h4>
                <ul className="space-y-2">
                  {advice.actionItems.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start text-sm text-gray-700">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons - Consistent Size on Web */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-2xl mx-auto">
        <button
          onClick={onRetry}
          className="group relative overflow-hidden bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-4 py-2 sm:px-4 sm:py-2 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 active:scale-[0.98] touch-manipulation sm:w-[220px]"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-teal-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative flex items-center justify-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="text-sm">Analyze Another Photo</span>
          </div>
        </button>

        <button
          onClick={generateReportImage}
          disabled={isGeneratingReport}
          className="group relative overflow-hidden bg-gradient-to-r from-violet-600 to-purple-600 text-white px-4 py-2 sm:px-4 sm:py-2 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 active:scale-[0.98] disabled:opacity-75 disabled:cursor-not-allowed disabled:transform-none touch-manipulation sm:w-[220px]"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-violet-700 to-purple-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <div className="relative flex items-center justify-center gap-2 sm:gap-3">
            {isGeneratingReport ? (
              <>
                <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm sm:text-base">Generating...</span>
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span className="text-sm sm:text-base">Save Report</span>
              </>
            )}
          </div>
        </button>
      </div>
    </div>
  );
}